import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    static targets = ['date', 'price', 'total', 'paymentType', 'radio'];
    static values = {
        contentIds: String
    }

    initialize() {
        super.initialize();

        this.calculate = (event) => this._calculate(event);
        this.recalculate = (event) => this._recalculate(event);

        if (this.hasRadioTarget) {
            this.radioTargets.forEach(el => {
                el.addEventListener('change', this.recalculate.bind(this));
            });
        }

        this.contentIdsValue = JSON.parse(this.contentIdsValue || '[]');
        this.pendingCalculations = 0;
        this.conversionTagAdded = false;
    }

    connect() {
        setTimeout(() => {
            if (!this.hasDateTarget && !this.conversionTagAdded) {
                this.checkAllCalculationsComplete();
            }
        }, 0);
    }

    dateTargetConnected(element) {
        const input = element.querySelector('input');

        if (input) {
            input.addEventListener('input', this.calculate);
            this._calculate(element);
        }
    }

    dateTargetDisconnected(element) {
        const input = element.querySelector('input');

        if (input) {
            input.removeEventListener('input', this.calculate);
        }
    }

    _recalculate(event) {
        const checkbox = event.target;
        const checkoutItemDiv = checkbox.closest('.checkout-item-container');
        const checkoutItem = checkoutItemDiv ? checkoutItemDiv.querySelector('.checkout-item') : null;

        const checkoutItemMonthly = checkoutItemDiv ? checkoutItemDiv.querySelector('.checkout-item-monthly-price') : null;
        const monthlyPriceContainer = checkoutItemMonthly ? checkoutItemMonthly.closest('.checkout-monthly-container') : null;

        const contractId = checkoutItem?.dataset.contractId;
        const groupId = checkoutItem?.dataset.groupId;
        const index = checkoutItem?.dataset.index;

        const advancePaymentContract = checkbox.dataset.advancePaymentContract;
        const advancePaymentItem = checkbox.dataset.advancePaymentItem;

        let selector = `[data-contract-price-target="price"][data-contract-id="${contractId}"]`;
        if (groupId) selector += `[data-group-id="${groupId}"]`;
        if (index) selector += `[data-index="${index}"]`;
        const priceElement = this.element.querySelector(selector);

        if (event.target.checked) {
            priceElement.dataset.payInAdvance = '1';
        } else {
            priceElement.dataset.payInAdvance = '0';
        }

        if (!priceElement.dataset.advancePrice) {
            let date = '';
            const groupWrapper = checkbox.closest('.group-wrapper');
            if (groupWrapper) {
                const dateInput = groupWrapper.querySelector('input[name*="[visitor_activation_date]"]');
                if (dateInput) date = dateInput.value;
            }

            const priceCalculationUrl = this.element.dataset.priceCalculationUrl;
            if (!priceElement || !priceCalculationUrl) return;

            let url = `${priceCalculationUrl}&contract_id=${contractId}`;
            if (groupId) url += `&group_id=${groupId}`;
            if (date) url += `&date=${encodeURIComponent(date)}`;

            url += `&advance_payment_contract=${advancePaymentContract}`;
            url += `&advance_payment_item=${advancePaymentItem}`;

            fetch(url)
                .then(response => response.json())
                .then(response => {
                    priceElement.dataset.advancePrice = response?.price || 0;
                    checkoutItemMonthly.innerHTML = `${response?.monthly_price || 0} &euro;`;
                    monthlyPriceContainer.classList.remove('d-none');
                    priceElement.innerHTML = `${response?.price || 0} &euro;`;

                    if (this.hasTotalTarget) {
                        let total = 0;

                        this.priceTargets.forEach(el => {
                            total += parseFloat(el.dataset.advancePrice || el.dataset.rawPrice || 0);
                        });

                        this.totalTarget.innerHTML = `${total.toFixed(2)} &euro;`;
                    }
                })
                .catch(console.error);
        } else {
            priceElement.innerHTML = `${priceElement.dataset.payInAdvance === '1' ? priceElement.dataset.advancePrice : priceElement.dataset.rawPrice || 0} &euro;`;
            priceElement.dataset.payInAdvance === '1' ? monthlyPriceContainer.classList.remove('d-none') : monthlyPriceContainer.classList.add('d-none');

            if (this.hasTotalTarget) {
                let total = 0;

                this.priceTargets.forEach(el => {
                    let priceToCalculate = el.dataset.payInAdvance === '1' ? el.dataset.advancePrice : el.dataset.rawPrice;
                    total += parseFloat(priceToCalculate || 0);
                });

                this.totalTarget.innerHTML = `${total.toFixed(2)} &euro;`;
            }
        }
    }

    _calculate(arg) {
        const priceCalculationUrl = this.element.dataset.priceCalculationUrl || undefined;

        if (priceCalculationUrl) {
            const element = arg instanceof Event ? arg.currentTarget.closest('[data-contract-price-target]') : arg;
            const input = element.querySelector('input');

            const index = element.dataset.index;
            const contractID = element.dataset.contractId;
            const groupID = element.dataset.groupId;
            const date = input.value;
            let selector = `[data-contract-price-target="price"][data-contract-id="${contractID}"]`;

            if (groupID) {
                selector += `[data-group-id="${groupID}"]`;
            }

            if (index) {
                selector += `[data-index="${index}"]`;
            }

            const priceElement = this.element.querySelector(selector);

            if (priceElement) {
                this.pendingCalculations++;

                fetch(`${priceCalculationUrl}&contract_id=${contractID}&date=${date}`)
                    .then(response => response.json())
                    .then(response => {
                        priceElement.dataset.rawPrice = response?.price || 0;
                        priceElement.innerHTML = `${response?.price || 0} &euro;`;

                        if (this.hasTotalTarget) {
                            let total = 0;

                            this.priceTargets.forEach(el => {
                                total += parseFloat(el.dataset.rawPrice || 0);
                            });

                            this.totalTarget.innerHTML = `${total.toFixed(2)} &euro;`;
                        }

                        this.pendingCalculations--;
                        this.checkAllCalculationsComplete();
                    })
                    .catch(error => {
                        console.error(error);
                        this.pendingCalculations--;
                        this.checkAllCalculationsComplete();
                    });
            }
        }
    }
    checkAllCalculationsComplete() {
        if (this.pendingCalculations === 0 && !this.conversionTagAdded) {
            if (window.location.hostname === 'svjc.lt' || window.location.hostname === 'www.svjc.lt') {
                this.addCheckoutConversionTag();
                this.conversionTagAdded = true;
            }
        }
    }

    calculatePriceWithoutRest() {
        if (this.hasTotalTarget) {
            let total = 0;

            this.priceTargets.forEach(el => {
                let priceToCalculate = el.dataset.payInAdvance === '1' ? el.dataset.advancePrice : el.dataset.rawPrice;
                total += parseFloat(priceToCalculate || 0);
            });

            this.totalTarget.innerHTML = `${total.toFixed(2)} &euro;`;
        }
    }

    addCheckoutConversionTag() {
        const totalValue = this.hasTotalTarget ?
            parseFloat(this.totalTarget.innerHTML.replace(/[^0-9.-]+/g, "")) : 0;

        const contentIds = this.contentIdsValue ? this.contentIdsValue.split(',') : [];
        const totalItems = this.priceTargets.length;

        // Facebook Pixel tracking
        fbq('track', 'InitiateCheckout', {
            value: totalValue.toFixed(2),
            currency: 'EUR',
            content_ids: contentIds,
            num_items: totalItems
        });

        const products = contentIds.map(id => {
            return {
                item_id: id,
                quantity: 1
            };
        });

        gtag('event', 'begin_checkout', {
            currency: 'EUR',
            value: totalValue,
            items: products
        });
    }

    resetConversionTracker() {
        this.conversionTagAdded = false;
    }
}
